import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/state/auth";
import { apiRequest } from "@/utils/apiClient";
import {
  HelpArticle,
  HelpArticleInput,
  HelpArticleSummary,
  HelpCollection,
  HelpCollectionInput,
  HelpCollectionSummary,
} from "@/types/help";

const QUERY_ROOT = ["admin", "help"] as const;

export const useHelpCollections = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, "collections"],
    queryFn: async () => {
      const data = await apiRequest<{ collections: HelpCollectionSummary[] }>(
        "/api/admin/help/collections?include_unpublished=true",
      );
      return data.collections;
    },
    enabled: Boolean(profile),
  });
};

export const useHelpCollection = (collectionId: string | null) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, "collection", collectionId],
    queryFn: async () => {
      if (!collectionId) {
        return null;
      }
      const data = await apiRequest<{ collection: HelpCollection }>(
        `/api/admin/help/collections/${collectionId}?include_unpublished=true`,
      );
      return data.collection;
    },
    enabled: Boolean(profile && collectionId),
  });
};

export const useCreateHelpCollection = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: HelpCollectionInput) => {
      const { collection } = await apiRequest<{ collection: HelpCollectionSummary }>(
        "/api/admin/help/collections",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      return collection;
    },
    onSuccess: (collection) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collections"] });
      client.setQueryData([...QUERY_ROOT, "collection", collection.collectionId], collection);
    },
  });
};

export const useUpdateHelpCollection = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ collectionId, payload }: { collectionId: string; payload: HelpCollectionInput }) => {
      const { collection } = await apiRequest<{ collection: HelpCollectionSummary }>(
        `/api/admin/help/collections/${collectionId}`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        },
      );
      return collection;
    },
    onSuccess: (collection) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collections"] });
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collection", collection.collectionId] });
    },
  });
};

export const useDeleteHelpCollection = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (collectionId: string) => {
      await apiRequest<void>(`/api/admin/help/collections/${collectionId}`, {
        method: "DELETE",
      });
      return collectionId;
    },
    onSuccess: (collectionId) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collections"] });
      client.removeQueries({ queryKey: [...QUERY_ROOT, "collection", collectionId] });
    },
  });
};

export const useCreateHelpArticle = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ collectionId, payload }: { collectionId: string; payload: HelpArticleInput }) => {
      const { article } = await apiRequest<{ article: HelpArticle }>(
        `/api/admin/help/collections/${collectionId}/articles`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      return { collectionId, article };
    },
    onSuccess: ({ collectionId, article }) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collections"] });
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collection", collectionId] });
      client.setQueryData([...QUERY_ROOT, "article", article.articleId], article);
    },
  });
};

export const useHelpArticle = (articleId: string | null) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, "article", articleId],
    queryFn: async () => {
      if (!articleId) {
        return null;
      }
      const { article } = await apiRequest<{ article: HelpArticle }>(`/api/admin/help/articles/${articleId}`);
      return article;
    },
    enabled: Boolean(profile && articleId),
  });
};

export const useUpdateHelpArticle = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId, payload }: { articleId: string; payload: Partial<HelpArticleInput> }) => {
      const { article } = await apiRequest<{ article: HelpArticle }>(
        `/api/admin/help/articles/${articleId}`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        },
      );
      return article;
    },
    onSuccess: (article) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collections"] });
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collection", article.collectionId] });
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "article", article.articleId] });
    },
  });
};

export const useDeleteHelpArticle = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ articleId }: { articleId: string }) => {
      await apiRequest<void>(`/api/admin/help/articles/${articleId}`, {
        method: "DELETE",
      });
      return articleId;
    },
    onSuccess: (articleId) => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT, "collections"] });
      client.removeQueries({ queryKey: [...QUERY_ROOT, "article", articleId] });
    },
  });
};

export const useSearchHelpArticles = (query: string, enabled: boolean) => {
  return useQuery({
    queryKey: [...QUERY_ROOT, "search", query],
    queryFn: async () => {
      if (!query) {
        return [] as HelpArticleSummary[];
      }
      const data = await apiRequest<{ results: HelpArticleSummary[] }>(
        `/api/admin/help/articles?query=${encodeURIComponent(query)}&limit=12`,
      );
      return data.results;
    },
    enabled,
  });
};
